import {
  DataGridWithSynchronousRowStore,
  DataGridWithSynchronousRowStoreProps,
  divWithStyle,
  ListViewRowOuterHeight,
  SelectionProviderChildProps,
  TypedColumn,
  TypedRowHandlerWithErrors,
  UsualDisplayGridProps,
} from '@tableau/data-catalog-shared';
import { TabStyles } from '@tableau/widgets';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';

export const defaultDataGridProps: Partial<DataGridWithSynchronousRowStoreProps<unknown, unknown>> = {
  leftMargin: 0,
  showHeaders: false,
  showRowBanding: false,
  showHorizontalGridLines: false,
};

export interface HeaderlessDisplayGridProps<TRowData, TError>
  extends UsualDisplayGridProps<TRowData, TError>, Partial<SelectionProviderChildProps> {
  readonly columns: TypedColumn<TRowData>[];
  readonly rowHeight?: number;
  readonly renderErrors: (errors: ReadonlyArray<TError>) => React.ReactNode;
}

export interface HeaderlessDisplayGridState<TError> {
  readonly gridErrors: ReadonlyArray<TError>;
}

// styles from vizportalclient, src/app/spaces/Explorer/ExplorerStyles.ts
// and overrides from reactDataGrid.styl
const DataGridContainer = divWithStyle('headerlessDataGridContainer', {
  flex: 1,
  color: TabStyles.Typography.Color.Primary,
  fontSize: TabStyles.Typography.Sizing.Default,
  $nest: {
    '.tb-react-dg-hrow': {
      border: 'none'
    },
    '.tb-react-dg-hcell-resizable': {
      border: 'none'
    },
    '.tb-react-dg-hcol': {
      border: 'none !important'
    },
    '.tb-react-dg-bcell': {
      paddingLeft: 0,
      paddingRight: 0,
      $nest: {
        '&:focus': {
          outline: 'none',
        },
      },
    },
  },
});

/**
 * A basic datagrid that does not show headers
 */
export class HeaderlessDisplayGrid<TRowData, TError>
  extends React.PureComponent<HeaderlessDisplayGridProps<TRowData, TError>, HeaderlessDisplayGridState<TError>> {

  public constructor(props: HeaderlessDisplayGridProps<TRowData, TError>) {
    super(props);
    this.state = {
      gridErrors: [],
    };
  }

  private renderErrorUI(): React.ReactNode {
    return this.props.renderErrors(this.state.gridErrors);
  }

  private handleGridErrors: TypedRowHandlerWithErrors<TRowData, TError> = (_, gridErrors) => {
    this.setState({ gridErrors });
  }

  public render(): React.ReactNode {

    if (this.state.gridErrors && this.state.gridErrors.length > 0) {
      return this.renderErrorUI();
    }

    const dataGridProps: DataGridWithSynchronousRowStoreProps<TRowData, TError> = {
      ...defaultDataGridProps,
      cols: this.props.columns,
      rowStore: this.props.rowStore,
      config: {
        colsPerChunk: this.props.columns.length,
        rowOuterHeight: this.props.rowHeight || ListViewRowOuterHeight,
      },
      showSpinnerWhileLoading: false,
      onError: this.handleGridErrors,
    };

    return (
      <DataGridContainer>
        {
          this.props.rowCount === 0
            ? <div>{this.props.emptyGridMessage || Messages.EmptyContentMessage()}</div>
            : <DataGridWithSynchronousRowStore {...dataGridProps} />
        }
      </DataGridContainer>
    );
  }

}
